// Keyboard navigation service with context-aware controls
import {ref} from 'vue';
import {params} from "@/systemInfo.js";
import {cycleRes} from "@/services/google/chrome.js";

// Current active context
const activeContext = ref('home'); // Possible values: 'home', 'app', 'options', 'modal'

// State variables
let currentFocusedElement = null;
let keyboardEventHandler = null;
let consumedEvent = null;
let mouseDisableTimer = null;
let isMouseDisabled = false;
let isEnabled = true;
let isProcessingKeyEvent = false;

// Callbacks for different contexts
const callbacks = {
    home: {
        onNavigate: null,
        onSelect: null,
        onEnsureVisible: null,
        onSetDefault: null,
        onShowDefaultPrompt: null,
        onOpenMenu: null
    },
    options: {
        onNavigate: null,
        onSelect: null,
        onScroll: null,
        onClose: null
    },
    modal: {
        onNavigate: null,
        onConfirm: null,
        onCancel: null
    },
    app: {
        onShowReturnHome: null
    },
};

// Button elements for different contexts
const contextButtons = {
    options: [],
    modal: []
};

// Current focused button index in the active context
const focusedButtonIndex = {
    options: 0,
    modal: 0
};

/**
 * Set the active context for keyboard navigation
 * @param {string} context - The context to set ('home', 'app', 'options', 'modal')
 * @param {Object} contextData - Additional data for the context
 */
export function setActiveContext(context, contextData = {}) {
    if (!['home', 'app', 'options', 'modal'].includes(context)) {
        console.error(`Invalid context: ${context}`);
        return;
    }

    console.log(`[KEYBOARD] Setting active keyboard context: ${context}`);
    activeContext.value = context;

    // Set up context-specific elements and focus
    if (context === 'options') {
        // Buttons are registered by the menu once it has rendered; drop the previous set
        contextButtons.options = contextData.buttons || [];
        focusedButtonIndex.options = 0;
        if (contextData.buttons) focusButton('options', 0);

        // Store close callback
        if (contextData.onClose) {
            callbacks.options.onClose = contextData.onClose;
        }
    } else if (context === 'modal' && contextData.buttons) {
        contextButtons.modal = contextData.buttons;
        focusedButtonIndex.modal = 0;
        focusButton('modal', 0);

        // Store confirm/cancel callbacks
        if (contextData.onConfirm) {
            callbacks.modal.onConfirm = contextData.onConfirm;
        }
        if (contextData.onCancel) {
            callbacks.modal.onCancel = contextData.onCancel;
        }
    }
}

/**
 * Get the current active context
 * @returns {string} The active context
 */
export function getActiveContext() {
    return activeContext.value;
}

/**
 * Focus a button in the specified context
 * @param {string} context - The context ('options', 'modal')
 * @param {number} index - The index of the button to focus
 */
function focusButton(context, index) {
    if (!contextButtons[context] || !contextButtons[context][index]) return;

    // Remove focus from all buttons in the context
    contextButtons[context].forEach(button => {
        button.classList.remove('keyboard-focused');
    });

    // Focus the specified button
    const button = contextButtons[context][index];
    button.classList.add('keyboard-focused');
    focusedButtonIndex[context] = index;
    button.focus();


    // Ensure the button is visible if in a scrollable container
    if (callbacks[context]?.onScroll) {
        callbacks[context].onScroll(button);
    }
}

/**
 * Global keyboard shortcuts handler
 * @param {KeyboardEvent} event - Keyboard event
 * @param {Object} options - Options for handling shortcuts
 * @returns {boolean} - Whether the event was handled
 */
export function handleGlobalShortcuts(event, options) {
    // Skip if already processing a key event
    if (isProcessingKeyEvent) return false;

    // Set flag to prevent multiple key events from being processed simultaneously
    isProcessingKeyEvent = true;

    try {
        const {
            showOptionsMenu,
            toggleOptionsMenu,
            closeOptionsMenu,
            showHomePrompt,
            cancelReturnHome,
            returnToHome,
            showDefaultPrompt,
            cancelSetDefault,
            confirmSetDefault,
            rebootSystem,
            showReturnHomePrompt,
            launchedApp,
            activeAppId,
            launchApp,
            allApps,
            showSetDefaultAppPrompt
        } = options;

        // Handle different key presses based on active context
        switch (activeContext.value) {
            case 'home':
                // Home screen context
                switch (event.key.toLowerCase()) {
                    case 'm':
                        toggleOptionsMenu();
                        return true;
                    case 'contextmenu':
                        toggleOptionsMenu();
                        return true;
                    // case 'd':
                    //     // Show set default app prompt
                    //     if (activeAppId && !showDefaultPrompt && !showOptionsMenu && isEnabled) {
                    //         const app = allApps.find(app => app.id === activeAppId);
                    //         if (app) {
                    //             showSetDefaultAppPrompt(app);
                    //             return true;
                    //         }
                    //     }
                    //     break;
                    case 'enter':
                        // Launch app
                        if (!showHomePrompt && !showDefaultPrompt && !showOptionsMenu &&
                            !launchedApp && activeAppId && isEnabled) {
                            const app = allApps.find(app => app.id === activeAppId);
                            if (app && (!app.disabled || app.disabled === false)) {
                                launchApp(app.id);
                                event.preventDefault();
                                return true;
                            }
                        }
                        break;
                }
                break;

            case 'app':
                // App context
                switch (event.key.toLowerCase()) {
                    case 'm':
                        toggleOptionsMenu();
                        return true;
                    case 'contextmenu':
                        toggleOptionsMenu();
                        return true;
                    case 'arrowup':
                        consumedEvent = event;
                        toggleOptionsMenu();
                        return true;
                    case 'h':
                    case 'escape':
                        if (launchedApp) {
                            showReturnHomePrompt();
                            return true;
                        }
                        break;
                }
                break;

            case 'options':
                // Options menu context
                switch (event.key.toLowerCase()) {
                    case 'm':
                    // case 'escape':
                    case 'contextmenu':
                        if (callbacks.options.onClose) {
                            callbacks.options.onClose();
                        } else {
                            closeOptionsMenu();
                        }
                        return true;
                    case 'arrowdown':
                        consumedEvent = event;
                        closeOptionsMenu();
                        return true;
                    case 'r':
                        rebootSystem();
                        return true;
                    case 'h':
                        if (launchedApp) {
                            showReturnHomePrompt();
                            return true;
                        }
                        break;
                    case 'd':
                        cycleRes()
                        break
                    case 'enter':
                        if (contextButtons.options.length > 0) {
                            const focusedButton = contextButtons.options[focusedButtonIndex.options];
                            if (focusedButton) {
                                focusedButton.click();
                                event.preventDefault();
                                return true;
                            }
                        }
                        break;
                }
                break;

            case 'modal':
                // Modal context
                switch (event.key.toLowerCase()) {
                    case 'escape':
                        // Cancel modal
                        if (showHomePrompt) {
                            cancelReturnHome();
                            return true;
                        } else if (showDefaultPrompt) {
                            cancelSetDefault();
                            return true;
                        }
                        break;
                    case 'enter':
                        if (contextButtons.modal.length > 0) {
                            const focusedButton = contextButtons.modal[focusedButtonIndex.modal];
                            console.log("FOCUSED BUTTON", focusedButton)
                            if (focusedButton) {
                                focusedButton.click();
                                event.preventDefault();
                                return true;
                            }
                        }
                        break;
                    case 'y':
                        // Confirm modal
                        if (showHomePrompt) {
                            returnToHome();
                            return true;
                        } else if (showDefaultPrompt) {
                            confirmSetDefault();
                            return true;
                        }
                        break;
                    case 'n':
                        // Cancel modal
                        if (showHomePrompt) {
                            cancelReturnHome();
                            return true;
                        } else if (showDefaultPrompt) {
                            cancelSetDefault();
                            return true;
                        }
                        break;
                }
                break;
        }

        return false;
    } finally {
        // Reset flag after processing
        isProcessingKeyEvent = false;
    }
}

/**
 * Initialize keyboard navigation
 * @param {Array} apps - Array of apps
 * @param {Function} onNavigate - Callback for navigation
 * @param {Function} onSelect - Callback for selection
 * @param {Function} onEnsureVisible - Callback to ensure element is visible
 * @param {Function} onSetDefault - Callback to set default app
 * @param {Function} onShowDefaultPrompt - Callback to show default app prompt
 * @returns {Object} - Keyboard navigation controller
 */
export function initKeyboardNavigation(apps, onNavigate, onSelect, onEnsureVisible, onSetDefault, onShowDefaultPrompt, onOpenMenu) {
    // Store callbacks for home context
    callbacks.home.onNavigate = onNavigate;
    callbacks.home.onSelect = onSelect;
    callbacks.home.onEnsureVisible = onEnsureVisible;
    callbacks.home.onSetDefault = onSetDefault;
    callbacks.home.onShowDefaultPrompt = onShowDefaultPrompt;
    callbacks.home.onOpenMenu = onOpenMenu;

    // Set up keyboard event listeners
    setupKeyboardListeners();

    // Initialize focus on the first app tile after a short delay to ensure DOM is ready
    setTimeout(() => {
        const appTiles = getAppTiles();
        if (appTiles.length > 0) {
            setFocusByElement(appTiles[0]);
        }
    }, 100);

    // Enable keyboard navigation
    isEnabled = true;

    // Set initial context
    setActiveContext('home');

    return {
        getCurrentFocusedElement: () => currentFocusedElement,
        setFocusByElement: (element) => {
            setFocusByElement(element);
        },
        setFocusByAppId: (appId) => {
            const appTiles = getAppTiles();
            const element = appTiles.find(el => el.dataset.appId === appId.toString());
            if (element) {
                setFocusByElement(element);
            }
        },
        setDefaultApp: () => {
            if (callbacks.home.onSetDefault && currentFocusedElement) {
                const appId = parseInt(currentFocusedElement.dataset.appId);
                if (!isNaN(appId)) {
                    callbacks.home.onSetDefault(appId);
                }
            }
        },
        showDefaultPrompt: () => {
            if (callbacks.home.onShowDefaultPrompt && currentFocusedElement) {
                const appId = parseInt(currentFocusedElement.dataset.appId);
                if (!isNaN(appId)) {
                    // Find the app object from the id
                    const app = window.appsRef.find(a => a.id === appId);
                    if (app) {
                        callbacks.home.onShowDefaultPrompt(app);
                    }
                }
            }
        },
        setEnabled: (enabled) => {
            isEnabled = enabled;
        },
        isEnabled: () => isEnabled,
        updateContext: (contextData) => {
            // Update context based on app state
            if (contextData.showOptionsMenu) {
                setActiveContext('options');
            } else if (contextData.showHomePrompt || contextData.showDefaultPrompt) {
                setActiveContext('modal');
            } else if (contextData.launchedApp) {
                setActiveContext('app');
            } else {
                setActiveContext('home');
            }
        }
    };
}

/**
 * Initialize options menu keyboard navigation
 * @param {Array} buttons - Array of button elements
 * @param {Function} onScroll - Callback to ensure button is visible
 * @param {Function} onClose - Callback to close the options menu
 */
export function initOptionsMenuKeyboard(buttons, onScroll, onClose) {
    contextButtons.options = buttons;
    callbacks.options.onScroll = onScroll;
    callbacks.options.onClose = onClose;

    // Focus the first button
    if (buttons.length > 0) {
        focusedButtonIndex.options = 0;
        focusButton('options', 0);
    }
}

/**
 * Initialize modal keyboard navigation
 * @param {Array} buttons - Array of button elements
 * @param {Function} onConfirm - Callback for confirm action
 * @param {Function} onCancel - Callback for cancel action
 */
export function initModalKeyboard(buttons, onConfirm, onCancel) {
    // contextButtons.modal = buttons;
    // callbacks.modal.onConfirm = onConfirm;
    // callbacks.modal.onCancel = onCancel;
    //
    // // Focus the first button
    // if (buttons.length > 0) {
    //     focusedButtonIndex.modal = 0;
    //     focusButton('modal', 0);
    // }
}

/**
 * Enable keyboard navigation
 */
export function enableKeyboardNavigation() {
    isEnabled = true;
}

/**
 * Disable keyboard navigation
 */
export function disableKeyboardNavigation() {
    isEnabled = false;
}

// Get all app tiles in their current visual order
function getAppTiles() {
    return Array.from(document.querySelectorAll('.app-tile'));
}

// Set focus to a specific app tile element
function setFocusByElement(element) {
    if (!element || !isEnabled) return;

    // Remove focus from current element
    if (currentFocusedElement) {
        currentFocusedElement.classList.remove('app-tile-active');
    }

    // Set new focused element
    currentFocusedElement = element;
    currentFocusedElement.classList.add('app-tile-active');

    // Call navigation callback with the app ID
    if (callbacks.home.onNavigate) {
        const appId = parseInt(currentFocusedElement.dataset.appId);
        if (!isNaN(appId)) {
            callbacks.home.onNavigate(appId);
        }
    }

    // Ensure the element is visible
    if (callbacks.home.onEnsureVisible) {
        callbacks.home.onEnsureVisible(currentFocusedElement);
    }
}

// Set up keyboard event listeners
function setupKeyboardListeners() {
    // Remove any existing listeners first
    if (keyboardEventHandler) {
        document.removeEventListener('keydown', keyboardEventHandler);
    }

    // Create new handler
    keyboardEventHandler = (event) => handleKeyDown(event);

    // Add new listener
    document.addEventListener('keydown', keyboardEventHandler);
}

// Disable mouse interaction with app tiles
function disableMouseInteraction() {
    isMouseDisabled = true;

    // Add a class to the body to indicate mouse is disabled
    document.body.classList.add('mouse-disabled');

    // Add pointer-events: none to all app tiles
    const appTiles = getAppTiles();
    appTiles.forEach(tile => {
        tile.style.pointerEvents = 'none';
    });

    // Reset the timer if it's already running
    if (mouseDisableTimer) {
        clearTimeout(mouseDisableTimer);
    }

    // Set a timer to re-enable mouse after 1 second
    mouseDisableTimer = setTimeout(() => {
        enableMouseInteraction();
    }, 1000);
}

// Enable mouse interaction with app tiles
function enableMouseInteraction() {
    isMouseDisabled = false;

    // Remove the class from the body
    document.body.classList.remove('mouse-disabled');

    // Remove pointer-events: none from all app tiles
    const appTiles = getAppTiles();
    appTiles.forEach(tile => {
        tile.style.pointerEvents = 'auto';
    });

    // Clear the timer
    if (mouseDisableTimer) {
        clearTimeout(mouseDisableTimer);
        mouseDisableTimer = null;
    }
}

// Handle keydown events
function handleKeyDown(event) {
    // Skip if keyboard navigation is disabled
    if (!isEnabled) return;

    // The shortcut handler already acted on this event, possibly changing context
    if (consumedEvent === event) return;

    // Only handle navigation keys if not in an input field
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
    }

    // Skip if any modifier key is pressed
    if (import.meta.env.VITE_ENVIRONMENT === 'prd' && (event.ctrlKey || event.altKey || event.shiftKey || event.metaKey || event.key.startsWith('F'))) {
        // Allow ctrl + shift + i for dev tools - is disabled in PRD for customer OU
        if(!(event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'i')) {
            event.preventDefault();
            return;
        }
    }

    // Skip if already processing a key event
    if (isProcessingKeyEvent) return;

    // Set flag to prevent multiple key events from being processed simultaneously
    isProcessingKeyEvent = true;

    try {
        let handled = false;

        // Handle navigation based on active context
        switch (activeContext.value) {
            case 'home':
                // Home screen navigation
                switch (event.key) {
                    case 'ArrowUp':
                        navigateUp();
                        handled = true;
                        break;
                    case 'ArrowDown':
                        navigateDown();
                        handled = true;
                        break;
                    case 'ArrowLeft':
                        navigateLeft();
                        handled = true;
                        break;
                    case 'ArrowRight':
                        navigateRight();
                        handled = true;
                        break;
                }
                break;

            case 'options':
                // Options menu navigation
                switch (event.key) {
                    case 'ArrowLeft':
                    case 'ArrowRight':
                        // Navigate between buttons
                        if (contextButtons.options.length > 0) {
                            const newIndex = event.key === 'ArrowLeft'
                                ? (focusedButtonIndex.options - 1 + contextButtons.options.length) % contextButtons.options.length
                                : (focusedButtonIndex.options + 1) % contextButtons.options.length;
                            focusButton('options', newIndex);
                            handled = true;
                        }
                        break;
                    case 'ArrowUp':
                    case 'ArrowDown':
                        // Scroll the options menu
                        const optionsMenu = document.querySelector('.options-menu');
                        const scrollAmt = optionsMenu.clientHeight * 0.1;
                        if (optionsMenu) {
                            const scrollAmount = event.key === 'ArrowUp' ? -scrollAmt : scrollAmt;
                            optionsMenu.scrollBy({
                                top: scrollAmount,
                                behavior: 'smooth'
                            });
                            handled = true;
                        }
                        break;
                }
                break;

            case 'modal':
                // Modal navigation
                switch (event.key) {
                    case 'ArrowLeft':
                    case 'ArrowRight':
                        // Navigate between buttons
                        if (contextButtons.modal.length > 0) {
                            const newIndex = event.key === 'ArrowLeft'
                                ? (focusedButtonIndex.modal - 1 + contextButtons.modal.length) % contextButtons.modal.length
                                : (focusedButtonIndex.modal + 1) % contextButtons.modal.length;
                            focusButton('modal', newIndex);
                            handled = true;
                        }
                        break;
                }
                break;
        }

        // If we handled an arrow key in home context, disable mouse interaction
        if (handled && activeContext.value === 'home') {
            disableMouseInteraction();
            event.preventDefault();
        } else if (handled) {
            event.preventDefault();
        }
    } finally {
        // Reset flag after processing
        isProcessingKeyEvent = false;
    }
}

// Calculate the grid layout
function getGridLayout() {
    const container = document.querySelector('.app-grid-container');
    if (!container) return {cols: params.cols, rows: 3}; // Default fallback

    const computedStyle = window.getComputedStyle(container);
    const gridTemplateColumns = computedStyle.getPropertyValue('grid-template-columns');

    // Count the number of columns by counting the number of "fr" or other units
    const colCount = gridTemplateColumns.split(' ').length;

    // Get all app tiles
    const appTiles = getAppTiles();

    // Calculate rows based on the number of tiles and columns
    const rowCount = Math.ceil(appTiles.length / colCount);

    return {cols: colCount, rows: rowCount};
}

// Navigation functions based on visual grid
function navigateUp() {
    if (!currentFocusedElement || !isEnabled) return;

    const appTiles = getAppTiles();
    const currentIndex = appTiles.indexOf(currentFocusedElement);
    if (currentIndex === -1) return;

    if (currentIndex === 0) {
        if (callbacks.home.onOpenMenu) callbacks.home.onOpenMenu();
        return;
    }

    const gridLayout = getGridLayout();
    const cols = params.cols || gridLayout.cols; // Use params.cols if available

    // Calculate the index of the tile above
    const targetIndex = currentIndex - cols;

    // If we're already in the first row, stay at the first item
    if (currentIndex < cols) {
        setFocusByElement(appTiles[0]);
    } else if (targetIndex >= 0) {
        // Normal case - move up one row
        setFocusByElement(appTiles[targetIndex]);
    }
}

function navigateDown() {
    if (!currentFocusedElement || !isEnabled) return;

    const appTiles = getAppTiles();
    const currentIndex = appTiles.indexOf(currentFocusedElement);
    if (currentIndex === -1) return;

    const gridLayout = getGridLayout();
    const cols = params.cols || gridLayout.cols; // Use params.cols if available
    const totalRows = Math.ceil(appTiles.length / cols);
    const lastRowStartIndex = (totalRows - 1) * cols;
    const lastItemIndex = appTiles.length - 1;

    // Calculate the index of the tile below
    const targetIndex = currentIndex + cols;

    // If we're already in the last row, stay at the last item
    if (currentIndex >= lastRowStartIndex) {
        setFocusByElement(appTiles[lastItemIndex]);
    } else if (targetIndex < appTiles.length) {
        // Normal case - move down one row
        setFocusByElement(appTiles[targetIndex]);
    } else {
        // If there's no item directly below, go to the last item
        setFocusByElement(appTiles[lastItemIndex]);
    }
}

function navigateLeft() {
    if (!currentFocusedElement || !isEnabled) return;

    const appTiles = getAppTiles();
    const currentIndex = appTiles.indexOf(currentFocusedElement);
    if (currentIndex === -1) return;

    const gridLayout = getGridLayout();
    const cols = params.cols || gridLayout.cols; // Use params.cols if available

    // Check if we're at the leftmost column
    if (currentIndex % cols > 0) {
        setFocusByElement(appTiles[currentIndex - 1]);
    }
}

function navigateRight() {
    if (!currentFocusedElement || !isEnabled) return;

    const appTiles = getAppTiles();
    const currentIndex = appTiles.indexOf(currentFocusedElement);
    if (currentIndex === -1) return;

    const gridLayout = getGridLayout();
    const cols = params.cols || gridLayout.cols; // Use params.cols if available

    // Calculate total items in current row
    const currentRow = Math.floor(currentIndex / cols);
    const itemsInCurrentRow = Math.min(cols, appTiles.length - (currentRow * cols));
    const isLastInRow = (currentIndex % cols) === itemsInCurrentRow - 1;

    // Check if we're at the rightmost column of the current row
    if (!isLastInRow) {
        setFocusByElement(appTiles[currentIndex + 1]);
    } else {
        // Wrap to the start of the same row
        const rowStartIndex = currentRow * cols;
        setFocusByElement(appTiles[rowStartIndex]);
    }
}

// Clean up function to remove event listeners
export function cleanupKeyboardNavigation() {
    if (keyboardEventHandler) {
        document.removeEventListener('keydown', keyboardEventHandler);
        keyboardEventHandler = null;
    }

    // Make sure to re-enable mouse interaction when cleaning up
    enableMouseInteraction();

    // Clear any pending timers
    if (mouseDisableTimer) {
        clearTimeout(mouseDisableTimer);
        mouseDisableTimer = null;
    }

    // Reset state
    currentFocusedElement = null;
    isEnabled = false;

    // Reset callbacks
    callbacks.home.onNavigate = null;
    callbacks.home.onSelect = null;
    callbacks.home.onEnsureVisible = null;
    callbacks.home.onSetDefault = null;
    callbacks.home.onShowDefaultPrompt = null;
    callbacks.home.onOpenMenu = null;
    callbacks.options.onNavigate = null;
    callbacks.options.onSelect = null;
    callbacks.options.onScroll = null;
    callbacks.options.onClose = null;
    callbacks.modal.onNavigate = null;
    callbacks.modal.onConfirm = null;
    callbacks.modal.onCancel = null;
    callbacks.app.onShowReturnHome = null;

    // Reset context buttons
    contextButtons.options = [];
    contextButtons.modal = [];

    // Reset focused button indices
    focusedButtonIndex.options = 0;
    focusedButtonIndex.modal = 0;
}